/* eslint-disable @typescript-eslint/no-namespace */
import type { Utility } from '../../types/utility';
import colors from '../colors';

export namespace Button {
  /** Button Component Variant Keys */
  export type VariantKeys = 'primary' | 'secondary' | 'navButton' | 'filter' | 'basic' | 'languageSwitcher';

  /** Button Component Variants */
  export type Variants = {
    [Key in VariantKeys]: Utility.CSSProperties;
  };

  /** Button Component Variant Modes */
  export type VariantModes = Utility.VariantKeyModes<VariantKeys>;

  export namespace Size {
    /** Button Size Component Variant Keys */
    export type VariantKeys = 'small' | 'medium' | 'large';

    /** Button Size Component Variants */
    export type Variants = {
      [Key in VariantKeys]: Utility.CSSProperties;
    };
  }
}

const buttonBase: Utility.CSSProperties = {
  display: 'inline-block',
  textDecoration: 'none',
  cursor: 'pointer',
  fontSize: 'inherit',
  lineHeight: 'lh1',
  transition: 'transform 0.15s ease-out',
  fontWeight: 'fw500',
  minWidth: 'max-content',
  textAlign: 'center',
};

const buttonOutline: Utility.CSSProperties = {
  '&:focus, &:focus-visible': {
    outline: '5px solid',
    outlineOffset: 0,
    outlineColor: 'coolGrey.10',
    '&::before': {
      position: 'absolute',
      content: '""',
      top: '-6px',
      bottom: '-6px',
      left: '-6px',
      right: '-6px',
      borderStyle: 'solid',
      borderWidth: '2px',
      borderColor: 'coolGrey.100',
      borderRadius: 'r4',
    },
  },
};

const primarySecondaryShared: Utility.CSSProperties = {
  ...buttonBase,
  ...buttonOutline,
  borderRadius: 'r4',
  margin: '1rem 0',
  outline: 'none',
  position: 'relative',
  borderStyle: 'solid',
  borderWidth: '1px',
  borderColor: 'coolGrey.100',
  '&:hover, &:active': {
    outlineColor: 'transparent',
    color: 'coolGrey.100',
    boxShadow: `inset 0 0 0 1px ${colors.coolGrey[60]}`,
    borderColor: 'coolGrey.60',
    backgroundColor: 'coolGrey.10',
  },
};

const disabledShared: Utility.CSSProperties = {
  cursor: 'default',
  outline: 'none',
  boxShadow: 'none',
  borderColor: 'transparent',
};

const base: Utility.VariantCSSKeys<Button.VariantKeys> = {
  primary: {
    ...primarySecondaryShared,
    color: 'white',
    backgroundColor: 'coolGrey.100',
    '&:disabled, &[aria-disabled="true"]': {
      ...disabledShared,
      color: 'coolGrey.80',
      backgroundColor: 'coolGrey.20',
    },
  },
  secondary: {
    ...primarySecondaryShared,
    color: 'coolGrey.100',
    backgroundColor: 'white',
    border: `1px solid ${colors.coolGrey[100]}`,
    '&:disabled, &[aria-disabled="true"]': {
      ...disabledShared,
      color: 'coolGrey.80',
      backgroundColor: 'coolGrey.20',
    },
  },
  navButton: {
    ...buttonBase,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    position: 'relative',
    width: '100%',
    height: '42px',
    backgroundColor: 'transparent',
    fontWeight: 'fw400',
    textDecoration: 'none',
    color: 'coolGrey.90',
    border: 'none',
    padding: 0,
    margin: 0,
    '&:focus': {
      outlineWidth: '1px',
      outlineStyle: 'solid',
    },
    '&:active, &:focus, &:hover': {
      borderRadius: 'r4',
      textDecoration: 'none',
    },
  },
  filter: {
    ...buttonBase,
    color: 'coolGrey.100',
    backgroundColor: 'white',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: 'coolGrey.100',
    borderRadius: 'r4',
    '&:active, &:focus, &:hover': {
      backgroundColor: 'primarySecondaryAccent',
    },
  },
  basic: {
    ...buttonBase,
    border: 'none',
    background: 'transparent',
    padding: 0,
    '&:active, &:focus': {
      borderColor: 'primary',
    },
  },
  languageSwitcher: {
    ...buttonBase,
    fontWeight: 'fw400',
    background: 'transparent',
    color: 'coolGrey.90',
    border: 'none',
    paddingTop: 0,
    paddingRight: 's8',
    paddingBottom: 0,
    paddingLeft: 's8',
  },
};

const chart: Utility.VariantCSSKeys<Button.VariantKeys> = {
  ...base,
  navButton: {
    ...base.navButton,
    '&:focus, &:focus-visible': {
      outlineColor: 'orange.60',
    },
    '&:active, &:focus, &:focus-visible, &:hover': {
      color: 'orange.70',
    },
  },
};

const co: Utility.VariantCSSKeys<Button.VariantKeys> = {
  ...base,
  navButton: {
    ...base.navButton,
    '&:focus, &:focus-visible': {
      outlineColor: 'blue.60',
    },
    '&:active, &:focus, &:focus-visible, &:hover': {
      color: 'blue.70',
    },
  },
};

const edu: Utility.VariantCSSKeys<Button.VariantKeys> = {
  ...base,
  navButton: {
    ...base.navButton,
    '&:focus, &:focus-visible': {
      outlineColor: 'indigo.60',
    },
    '&:active, &:focus, &:focus-visible, &:hover': {
      color: 'indigo.70',
    },
  },
};

const scale: Utility.VariantCSSKeys<Button.VariantKeys> = {
  ...base,
  navButton: {
    ...base.navButton,
    '&:focus, &:focus-visible': {
      outlineColor: 'indigo.60',
    },
    '&:active, &:focus, &:focus-visible, &:hover': {
      color: 'indigo.70',
    },
  },
};

const spark: Utility.VariantCSSKeys<Button.VariantKeys> = {
  ...base,
  navButton: {
    ...base.navButton,
    '&:focus, &:focus-visible': {
      outlineColor: 'red.60',
    },
    '&:active, &:hover, &:focus, &:focus-visible': {
      color: 'red.70',
    },
  },
};

const button: Button.VariantModes = {
  products: {
    chart,
    chartblog: chart,
    co,
    edu,
    scale,
    spark,
  },
};

export const buttonVariants = Object.keys(base) as Button.VariantKeys[];

export default button;
